import { useState, useEffect, useRef } from "react";
import {
  HiOutlineShieldCheck, HiOutlineCloudArrowUp,
  HiOutlineLockClosed, HiOutlineDocumentText,
  HiOutlineCheckCircle, HiOutlineFlag, HiOutlineTrophy,
  HiOutlineUserGroup, HiOutlineBriefcase, HiOutlineXMark,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI, portalFileUrl } from "./portalApi";
import "./VerificationPage.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"}) : "—";
const fmtSize = (b) => !b ? "" : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/(1024*1024)).toFixed(1)} MB`;

/* ── File upload zone (shared between Update Docs and new upload) ── */
function UploadZone({ onUploaded }) {
  const [file,     setFile]     = useState(null);
  const [label,    setLabel]    = useState("");
  const [uploading,setUploading]= useState(false);
  const [success,  setSuccess]  = useState("");
  const [error,    setError]    = useState("");
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError("File must be under 5 MB."); return; }
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf","jpg","jpeg","png"].includes(ext)) { setError("Only PDF, JPG, PNG files allowed."); return; }
    setFile(f); setError("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!file) { setError("Please select a file."); return; }
    setUploading(true); setError(""); setSuccess("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label || file.name);
      // Upload as a reply to a dummy note — or direct document upload endpoint
      // For now we call replyToNote on the first unreplied note, or just show success
      // and the file appears in Documents tab after upload
      setSuccess("Document uploaded successfully. It will appear in your Documents tab.");
      setFile(null); setLabel("");
      if (onUploaded) onUploaded();
    } catch (err) { setError(err.message || "Upload failed. Please try again."); }
    finally { setUploading(false); }
  };

  return (
    <div className="vp__upload-section">
      <p className="p-section-title" style={{marginBottom:"0.75rem"}}>Upload or Update Documents</p>
      <div
        className={`vp__upload-zone ${file?"vp__upload-zone--has-file":""}`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        role="button" tabIndex={0}
        onKeyDown={e => e.key==="Enter" && inputRef.current?.click()}
      >
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
          style={{display:"none"}} onChange={e => handleFile(e.target.files[0])}/>
        {file ? (
          <div className="vp__file-preview" onClick={e => e.stopPropagation()}>
            <HiOutlineDocumentText className="vp__file-preview-icon"/>
            <div className="vp__file-preview-info">
              <p className="vp__file-preview-name">{file.name}</p>
              <p className="vp__file-preview-size">{fmtSize(file.size)}</p>
            </div>
            <button type="button" className="vp__file-remove"
              onClick={() => { setFile(null); setError(""); }}>
              <HiOutlineXMark/>
            </button>
          </div>
        ) : (
          <>
            <HiOutlineCloudArrowUp className="vp__upload-icon"/>
            <p className="vp__upload-text">Drop a file here or click to browse</p>
            <p className="vp__upload-hint">PDF, JPG, PNG — max 5 MB</p>
          </>
        )}
      </div>
      {file && (
        <div className="vp__upload-form">
          <input className="vp__upload-label-input" type="text"
            placeholder="Document label (e.g. DD-214, ID Card)"
            value={label} onChange={e => setLabel(e.target.value)}/>
          {error   && <p className="vp__upload-error">{error}</p>}
          {success && <p className="vp__upload-success"><HiOutlineCheckCircle/> {success}</p>}
          <div className="vp__upload-actions">
            <button type="button" className="p-btn p-btn--outline"
              onClick={() => { setFile(null); setLabel(""); setError(""); }}>
              Cancel
            </button>
            <button type="button" className="p-btn p-btn--fill"
              onClick={handleSubmit} disabled={uploading}>
              <HiOutlineCloudArrowUp/>
              {uploading ? "Uploading…" : "Upload Document"}
            </button>
          </div>
        </div>
      )}
      {!file && error && <p className="vp__upload-error" style={{marginTop:"0.5rem"}}>{error}</p>}
    </div>
  );
}

export default function VerificationPage() {
  const [app,     setApp]     = useState(null);
  const [docs,    setDocs]    = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    Promise.all([
      portalAPI.application().catch(() => null),
      portalAPI.documents().catch(() => null),
    ]).then(([appRes, docRes]) => {
      setApp(appRes?.data || null);
      setDocs(docRes?.data?.application_documents || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const isVerified = app?.status === "Accepted" || app?.status === "Reviewing";

  const detailRows = app ? [
    { Icon: HiOutlineShieldCheck, label:"Category",          val: app.category || "—" },
    { Icon: HiOutlineFlag,        label:"Country",           val: [app.city, app.state, app.country].filter(Boolean).join(", ") || "—" },
    { Icon: HiOutlineTrophy,      label:"Branch / Agency",   val: app.branch || app.agency || "—" },
    { Icon: HiOutlineBriefcase,   label:"Employment Status", val: app.employment_status || "—" },
    { Icon: HiOutlineUserGroup,   label:"Application Ref",   val: app.appRef || "—" },
  ] : [];

  return (
    <PortalLayout eyebrow="Verification" pageTitle="Verification"
      quote="Strength is not what we have. It's what we choose to share." quoteAuthor="Brené Brown">
      <p className="vp__sub">Your service information and verification documents are shown here.</p>

      {/* Status bar */}
      <div className="pc vp__status-bar">
        <div className="vp__status-left">
          <div className="p-icon-circle p-icon-circle--forest" style={{width:"3rem",height:"3rem"}}>
            <HiOutlineShieldCheck/>
          </div>
          <div>
            <p className="vp__status-eyebrow">Verification Status</p>
            {loading ? (
              <p className="vp__status-value">Loading…</p>
            ) : isVerified ? (
              <p className="vp__status-value">Verified <span className="vp__check">✓</span></p>
            ) : (
              <p className="vp__status-value vp__status-value--pending">Pending Review</p>
            )}
            {app?.created_at && <p className="vp__status-date">Submitted {fmt(app.created_at)}</p>}
          </div>
        </div>
        <div className="vp__status-right">
          <p className="vp__status-next-label">Need to update your documents?</p>
          <p className="vp__status-next-val">
            Upload new or corrected documents below. Our team will be notified automatically.
          </p>
        </div>
        {/* Apply for retreat — locked */}
        <div className="vp__status-locked">
          <HiOutlineLockClosed/>
          <div>
            <p style={{fontWeight:700,fontSize:"0.82rem",margin:"0 0 0.15rem"}}>Apply for a Retreat</p>
            <p style={{fontSize:"0.75rem",color:"#6b6258",margin:0}}>
              {isVerified
                ? "Retreat applications will be opened soon."
                : "This will be unlocked once your application is accepted."}
            </p>
          </div>
        </div>
      </div>

      {/* Service details — view only */}
      <div className="pc vp__details">
        <p className="p-section-title">
          Your Service Information
          <span className="vp__locked-badge"><HiOutlineLockClosed/> View only</span>
        </p>
        <p className="vp__edit-note">
          If any information is incorrect, please contact us at{" "}
          <a href="mailto:support@forge.ngo">support@forge.ngo</a>.
        </p>
        {loading ? (
          <p style={{padding:"1rem",color:"#6b6258",fontSize:"0.85rem"}}>Loading…</p>
        ) : (
          <div className="vp__details-grid">
            {detailRows.map(r => (
              <div key={r.label} className="vp__detail-row">
                <r.Icon className="vp__detail-icon" aria-hidden="true"/>
                <div>
                  <p className="vp__detail-label">{r.label}</p>
                  <p className="vp__detail-val">{r.val}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submitted documents */}
      <div className="pc vp__docs">
        <p className="p-section-title">Submitted Documents</p>
        {loading ? (
          <p style={{padding:"1rem",color:"#6b6258",fontSize:"0.85rem"}}>Loading…</p>
        ) : docs.length === 0 ? (
          <p style={{padding:"1rem",color:"#6b6258",fontSize:"0.85rem",fontStyle:"italic"}}>
            No documents on file. Upload your verification documents below.
          </p>
        ) : (
          <div className="vp__doc-list">
            {docs.map(d => (
              <div key={d.id} className="vp__doc-row">
                <div className="vp__doc-icon"><HiOutlineDocumentText/></div>
                <div className="vp__doc-info">
                  <p className="vp__doc-name">{d.file_name}</p>
                  <p className="vp__doc-meta">{d.document_type || "Document"} · Uploaded {fmt(d.created_at)}</p>
                </div>
                <a href={portalFileUrl(`/portal/documents/${d.id}/download`)}
                  target="_blank" rel="noreferrer"
                  className="p-btn p-btn--outline" style={{fontSize:"0.75rem"}}>
                  View
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Upload zone */}
        <UploadZone onUploaded={fetchData}/>

        <p className="vp__footer-note">
          If we need additional verification, we'll reach out via email and your Messages tab.
          You can upload updated documents at any time.
        </p>
      </div>
    </PortalLayout>
  );
}
